import { Download } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../components/Toast.tsx';
import { useLocale, useT } from '../i18n/index.ts';
import { Button } from '../ui/Button.tsx';
import { formatExportList, formatExportRowCount } from './list.ts';
import type { ExportResult } from './types.ts';

/**
 * Secondary download button used on /requests, /usage and /billing.
 * Progress lives on the button (`loading` + working copy); cap / error /
 * success report through toasts so the three surfaces stay consistent.
 */
export function ExportExcelButton(props: {
  readonly testId: string;
  readonly disabled?: boolean;
  readonly onExport: (onProgress: (rows: number) => void) => Promise<ExportResult>;
}): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const [working, setWorking] = useState(false);
  const [rowsFetched, setRowsFetched] = useState<number | null>(null);

  async function handleClick(): Promise<void> {
    if (working) return;
    setWorking(true);
    setRowsFetched(null);
    try {
      const result = await props.onExport((rows) => {
        setRowsFetched(rows);
      });
      if (result.status === 'cap') {
        toast.error(t('export.cap'));
        return;
      }
      if (result.status === 'error') {
        toast.error(t('export.error'));
        return;
      }
      if (result.status === 'rate_unavailable') {
        toast.error(
          t('export.rate_unavailable', { months: formatExportList(result.months, locale) }),
        );
        return;
      }
      if (result.status === 'unknown_models') {
        toast.error(
          t('export.unknown_models', { models: formatExportList(result.models, locale) }),
        );
        return;
      }
      if (result.status === 'aggregate_cross_month_rate') {
        toast.error(
          t('export.aggregate_cross_month_rate', {
            months: formatExportList(result.months, locale),
          }),
        );
        return;
      }
      toast.success(t('export.done'));
    } catch {
      toast.error(t('export.error'));
    } finally {
      setWorking(false);
    }
  }

  const label =
    working && rowsFetched !== null
      ? t('export.working_rows', { rows: formatExportRowCount(rowsFetched) })
      : working
        ? t('export.working')
        : t('export.idle');

  return (
    <Button
      variant="secondary"
      loading={working}
      disabled={props.disabled === true || working}
      onClick={() => {
        void handleClick();
      }}
      data-testid={props.testId}
    >
      {working ? null : <Download aria-hidden="true" className="h-3.5 w-3.5" />}
      {label}
    </Button>
  );
}
